import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Ticket, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketFeeSettingsCard({ token }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/ticket-fees/platform`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const c = res.data.config;
        setCfg({ percent: String(c.percent), flat: String((c.flat_cents / 100).toFixed(2)), pp: String(c.payment_processing_percent) });
      })
      .catch(() => toast.error('Could not load ticket fee settings'));
  }, [token]);

  const save = async () => {
    const percent = parseFloat(cfg.percent), flat = parseFloat(cfg.flat), pp = parseFloat(cfg.pp);
    if ([percent, flat, pp].some((v) => isNaN(v) || v < 0)) {
      toast.error('All values must be valid non-negative numbers');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/ticket-fees/platform`, {
        percent, flat_cents: Math.round(flat * 100), payment_processing_percent: pp,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket fees updated — homepage & checkout now use the new rates');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-ticket-fees">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Ticket className="h-5 w-5 text-indigo-500" />
          Ticket Service Fees
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Platform-wide default fee on every sold ticket (buyers pay at checkout). The payment processing % should cover Stripe&apos;s 2.9% + 30¢ cut. These rates are shown publicly on the homepage and drive real checkout math. Per-event overrides (Event → Ticket Fees) still take priority.
        </p>
        {!cfg ? (
          <div className="h-10 flex items-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 w-28">
              <Label className="text-xs">Service fee %</Label>
              <Input type="number" min={0} max={50} step="0.1" value={cfg.percent}
                onChange={(e) => setCfg({ ...cfg, percent: e.target.value })} data-testid="ticket-fee-percent-input" />
            </div>
            <div className="space-y-1 w-28">
              <Label className="text-xs">Flat $ / ticket</Label>
              <Input type="number" min={0} step="0.01" value={cfg.flat}
                onChange={(e) => setCfg({ ...cfg, flat: e.target.value })} data-testid="ticket-fee-flat-input" />
            </div>
            <div className="space-y-1 w-28">
              <Label className="text-xs">Processing %</Label>
              <Input type="number" min={0} max={10} step="0.1" value={cfg.pp}
                onChange={(e) => setCfg({ ...cfg, pp: e.target.value })} data-testid="ticket-fee-processing-input" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="ticket-fee-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
        {cfg && (
          <p className="text-xs text-muted-foreground" data-testid="ticket-fee-preview">
            Buyers will pay: <strong>{cfg.percent || 0}% + ${Number(cfg.flat || 0).toFixed(2)}</strong> per ticket, plus <strong>{cfg.pp || 0}%</strong> payment processing.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
